import { LogoSVG } from '@/components/logo/logo';
import { Button, LinearProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const START_EVENT = 'kara:update-start';

/// Lance la mise à jour et ouvre sa fenêtre, depuis le bandeau comme depuis
/// les réglages.
export function startUpdate(): void {
  window.dispatchEvent(new Event(START_EVENT));
  window.api.update();
}

/**
 * Le logiciel télécharge ses mises à jour tout seul et les installe à la
 * fermeture — sans jamais le dire. Un utilisateur bloqué sur une version
 * défaillante n'avait donc aucun moyen de savoir qu'un redémarrage suffisait,
 * et réinstallait. Ce bandeau le prévient et propose le redémarrage.
 *
 * Une fois la mise à jour lancée, sa fenêtre prend le relais : téléchargement,
 * puis installation et redémarrage. L'installateur tourne en silence, sa
 * fenêtre Windows ne s'ouvre plus.
 */
export default function UpdateBanner(): JSX.Element | null {
  const { t } = useTranslation('settings');
  const [ready, setReady] = useState(false);
  const [started, setStarted] = useState(false);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);

  useEffect(() => window.api.onUpdateDownloaded(() => setReady(true)), []);
  // Le téléchargement automatique envoie lui aussi son avancement : il ne
  // s'affiche qu'une fois la mise à jour demandée.
  useEffect(() => window.api.onUpdateProgress(setProgress), []);
  useEffect(() => {
    const open = () => {
      setStarted(true);
      setProgress((p) =>
        !p || p.phase === 'error'
          ? {
              phase: 'downloading',
              percent: ready ? 100 : 0,
              bytesPerSecond: 0,
            }
          : p,
      );
    };
    window.addEventListener(START_EVENT, open);
    return () => window.removeEventListener(START_EVENT, open);
  }, [ready]);

  if (started && progress) {
    return (
      <UpdateWindow progress={progress} onClose={() => setStarted(false)} />
    );
  }
  if (!ready) return null;
  return (
    // `above-tabbar` vaut `bottom-4` sur ordinateur et s'écarte de la barre
    // d'onglets et de la barre gestuelle sous 900px, où `bottom-4` posait le
    // bandeau sur la navigation.
    <div className="above-tabbar fixed left-1/2 z-[1400] flex max-w-[calc(100vw-1.5rem)] -translate-x-1/2 items-center gap-3 rounded-lg border border-white/10 bg-neutral-900 px-4 py-2 text-[12px] text-neutral-200 shadow-lg">
      <span>{t('updateReady')}</span>
      <Button size="small" variant="contained" onClick={startUpdate}>
        {t('restartNow')}
      </Button>
    </div>
  );
}

/// Fenêtre de mise à jour, aux couleurs de Kara.
function UpdateWindow({
  progress,
  onClose,
}: {
  progress: UpdateProgress;
  onClose: () => void;
}) {
  const { t } = useTranslation('settings');
  const failed = progress.phase === 'error';
  const title = failed
    ? t('updateFailed')
    : progress.phase === 'downloading'
      ? t('updateDownloading')
      : t('updateInstalling');
  const hint = failed
    ? t('updateFailedHint')
    : progress.phase === 'downloading'
      ? t('updateDownloadingHint')
      : t('updateInstallingHint');

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm">
      <div className="w-[380px] max-w-full rounded-[12px] border border-white/[0.08] bg-[#111114] p-6 font-main shadow-[0_24px_64px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3">
          <LogoSVG size={38} />
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-neutral-100">
              {title}
            </div>
            <div className="text-[12px] text-neutral-500">{hint}</div>
          </div>
        </div>

        {failed ? (
          <div className="mt-5 flex justify-end">
            <Button size="small" color="inherit" onClick={onClose}>
              {t('updateClose')}
            </Button>
          </div>
        ) : (
          <div className="mt-5">
            <LinearProgress
              variant={
                progress.phase === 'downloading' ? 'determinate' : 'indeterminate'
              }
              value={
                progress.phase === 'downloading' ? progress.percent : undefined
              }
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#293DED',
                  borderRadius: 3,
                },
              }}
            />
            <div className="mt-2 flex h-4 justify-between text-[11px] tabular-nums text-neutral-500">
              {progress.phase === 'downloading' && (
                <>
                  <span>{Math.round(progress.percent)} %</span>
                  {progress.bytesPerSecond > 0 && (
                    <span>
                      {(progress.bytesPerSecond / 1_000_000).toFixed(1)} M
                      {t('byteLetter', { ns: 'base' })}/s
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
